import { Star } from '@mui/icons-material'
import { Tooltip } from '@mui/material'
import { observer } from 'mobx-react-lite'
import React from 'react'
import { getI18n, useTranslation } from 'react-i18next'
import { ILinkedSkill, IOtherEmployerDetails, IPrivateProjectFormPackage } from '../../../interfaces/projects/IProjects'
import { useStore } from '../../../Provider'
import { Data, OPNewTable } from '../../OPcomponents/OPNewTable'
import AddNewEmployerJobTitlesProject from './newemployerjobtitlesproject/AddNewEmployerJobTitlesProject'
import { formatDateFi } from './workHistoryHelpers'

/*
TODO:
- Rename employment history keyproject as keyemployments in frontend
- Skills as number + tooltip with skill names in private/public projects
*/

const EmployersJobTitlesWorkHistoryTable = (props: { rowId: number | undefined }): JSX.Element => {
  const rootStore = useStore()
  const { rowId } = props
  const { t } = useTranslation()
  const { validatedUser, user } = rootStore.loginStore
  const { projects, getDetailedUserProjects, allOtherEmployerDetails } = rootStore.projectStore

  const {
    filteredEmployersJobTitlesWorkHistories,
    employersJobTitlesWorkHistorySortColumn,
    employersJobTitlesWorkHistorySortDirection,
    setEmployerJobTitlesWorkHistorySortColumn,
    toggleEmployerJobTitlesWorkHistorySortDirection,
  } = rootStore.personStore

  // Mapping header i18n keys to underlying data fields in store
  const columnMap: Record<string, string> = {
    [t('Skills.EmployerJob')]: 'projectName',
    [t('Projects.ProjectStartDate')]: 'empDetails[0].startDate',
    [t('Projects.ProjectEndDate')]: 'empDetails[0].endDate',
    [t('WorkHistory.Expirience')]: 'daysWorked',
    [t('Projects.KeyEmployment')]: 'isKeyProject',
    [t('WorkHistory.Skills')]: 'linkedSkills.length',
    [t('WorkHistory.Roles')]: 'roles.length',
  }

  const onRequestSort = (headerText: string) => {
    const mapped = columnMap[headerText]
    if (!mapped) return
    if (employersJobTitlesWorkHistorySortColumn === mapped) {
      toggleEmployerJobTitlesWorkHistorySortDirection()
    } else {
      setEmployerJobTitlesWorkHistorySortColumn(mapped)
    }
  }

  const tableRows: Data[] = []

  const onRowClickHandler = async (id: number | string) => {
    const clickedProject = projects.find((proj) => proj.project.projectId === id)?.project
    if (clickedProject) {
      const userDetails = await getDetailedUserProjects(validatedUser, clickedProject?.projectId)
      const employerDetails =
        allOtherEmployerDetails.find((emp) => emp.employerId === clickedProject?.employerId) ?? ({} as IOtherEmployerDetails)

      const projectInfo: IPrivateProjectFormPackage = {
        project: clickedProject,
        userDetails,
        employerDetails,
      }
      return projectInfo
    }
  }

  filteredEmployersJobTitlesWorkHistories.forEach((work) => {
    tableRows.push({
      ['key']: work.projectId,
      ['id']: work.projectId,
      rows: {
        [t('Skills.EmployerJob')]: { text: work.projectName.replace(`- ${user.name}`, ''), width: '54%' },
        [t('Projects.ProjectStartDate')]: { text: formatDateFi(work.empDetails?.[0]?.startDate), width: '10%' },
        [t('Projects.ProjectEndDate')]: { text: formatDateFi(work.empDetails?.[0]?.endDate), width: '10%' },
        [t('WorkHistory.Expirience')]: {
          text: `${work.daysWorked || 0} ${t('Date.ExperienceDays')}`,
          width: '8%',
          align: 'right' as const,
        },
        [t('Projects.KeyEmployment')]: {
          text: work.isKeyProject ? <Star color='primary' /> : '',
          width: '9%',
          align: 'center' as const,
        },
        [t('WorkHistory.Skills')]: {
          text: (
            <Tooltip
              title={work.linkedSkills
                .map((s: ILinkedSkill) => (getI18n().language === 'en' && s.skillNameEng?.length ? s.skillNameEng : s.skillNameFi))
                .join(', ')}
              placement='bottom'
              arrow
            >
              <span>{work.linkedSkills.length}</span>
            </Tooltip>
          ),
          width: '5%',
          align: 'right' as const,
        },
        [t('WorkHistory.Roles')]: {
          text: (
            <Tooltip
              title={
                work.roles?.map((r) => (getI18n().language === 'en' && r.roleNameEng?.length ? r.roleNameEng : r.roleNameFi)).join(', ') ||
                ''
              }
              placement='bottom'
              arrow
            >
              <span>{work.roles?.length || 0}</span>
            </Tooltip>
          ),
          width: '5%',
          align: 'right' as const,
        },
      },
    })
  })

  return tableRows.length ? (
    <OPNewTable
      rows={tableRows}
      curLength={tableRows.length}
      profileclickedId={rowId}
      onRowClick={onRowClickHandler}
      FormBase={AddNewEmployerJobTitlesProject}
      orderByControlled={Object.keys(columnMap).find((k) => columnMap[k] === employersJobTitlesWorkHistorySortColumn) || ''}
      orderControlled={employersJobTitlesWorkHistorySortDirection === 'ascending' ? 'asc' : 'desc'}
      onSortRequest={onRequestSort}
    />
  ) : (
    <span>{t('WorkHistory.NoEmployersJobTitles')}</span>
  )
}

export default observer(EmployersJobTitlesWorkHistoryTable)
